import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  Calendar, 
  Plus, 
  Search, 
  User, 
  Phone, 
  MapPin, 
  Clock, 
  IndianRupee, 
  CheckCircle2, 
  XCircle, 
  AlertCircle, 
  Loader2, 
  X, 
  Key, 
  UserCheck, 
  Play, 
  Ban, 
  ChevronRight, 
  Briefcase, 
  Filter, 
  RefreshCw,
  Copy,
  Zap,
  Check,
  Printer,
  QrCode,
  Share2,
  Send,
  ExternalLink,
  CreditCard
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { ServiceBillingPanel } from './ServiceBillingPanel';
import { ServiceCustomerSearchDropdown } from './ServiceCustomerSearchDropdown';
import { JobKitManager } from './JobKitManager';
import { ServiceBillPrintModal } from './ServiceBillPrintModal';
import { ServiceBookingMessagesPanel } from './ServiceBookingMessagesPanel';

export type BookingStatus = 
  | 'requested' 
  | 'assigned' 
  | 'accepted' 
  | 'rejected' 
  | 'in_progress' 
  | 'completed' 
  | 'cancelled' 
  | 'no_show';

export type BookingMode = 'scheduled' | 'on_demand';

export interface ServiceBookingLineForm {
  id: string;
  catalog_item_id: string;
  quantity: string;
  custom_total: string;
  notes: string;
}

export interface ServiceBooking {
  id: string;
  tenant_id: string;
  branch_id: string;
  booking_number: string;
  source?: string;
  customer_id?: string | null;
  customer_user_id?: string | null;
  customer_name: string;
  customer_phone?: string | null;
  customer_address?: string | null;
  customer_lat?: number | null;
  customer_lng?: number | null;
  category_id?: string | null;
  catalog_item_id: string;
  professional_id?: string | null;
  assigned_by?: string | null;
  booking_mode: BookingMode;
  scheduled_at?: string | null;
  status: BookingStatus;
  quoted_price: number;
  final_price?: number | null;
  invoice_id?: string | null;
  start_otp?: string | null;
  complete_otp?: string | null;
  cancelled_reason?: string | null;
  created_at: string;
  updated_at?: string;
  assigned_at?: string | null;
  accepted_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  quantity?: number | null;
  customer_notes?: string | null;
  advance_amount?: number | null;
  advance_applied?: number | null;
  advance_refunded?: number | null;
  advance_mode?: string | null;
  advance_received_at?: string | null;
  payment_status?: string | null;
  booking_charge_due?: number | null;
  booking_charge_waived?: boolean | null;
  booking_charge_waive_reason?: string | null;
  razorpay_payment_link_id?: string | null;
  razorpay_payment_link_short_url?: string | null;
  razorpay_payment_link_status?: string | null;
  booking_charge_link_id?: string | null;
  booking_charge_link_url?: string | null;
  booking_charge_link_status?: string | null;
  service_booking_lines?: any[] | null;
}

export interface CatalogItem {
  id: string;
  service_name: string;
  base_price: number;
  category_id?: string | null;
  is_active: boolean;
  allow_custom_price?: boolean | null;
  unit_id?: string | null;
}

export interface ProfessionalItem {
  id: string;
  full_name: string;
  phone?: string | null;
  is_active: boolean;
}

interface ServiceBookingsViewProps {
  branchId?: string | null;
}

export function ServiceBookingsView({ branchId }: ServiceBookingsViewProps = {}) {
  const { currentTenantId, activeBranchId } = useAuth();
  const supabase = getSupabaseClient();

  // Data states
  const [bookings, setBookings] = useState<ServiceBooking[]>([]);
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [professionals, setProfessionals] = useState<ProfessionalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvedBranchId, setResolvedBranchId] = useState<string | null>(branchId || activeBranchId || null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [modeFilter, setModeFilter] = useState<string>('all');

  // Detail Drawer / Modal
  const [selectedBooking, setSelectedBooking] = useState<ServiceBooking | null>(null);

  // Action states in detail drawer
  const [assignProId, setAssignProId] = useState<string>('');
  const [startOtpInput, setStartOtpInput] = useState<string>('');
  const [showStartOtpModal, setShowStartOtpModal] = useState(false);
  const [completeOtpInput, setCompleteOtpInput] = useState<string>('');
  const [finalPriceInput, setFinalPriceInput] = useState<string>('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [cancelReasonInput, setCancelReasonInput] = useState<string>('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // New Booking Form V2 States
  const [showNewBookingModal, setShowNewBookingModal] = useState(false);
  const [serviceLines, setServiceLines] = useState<ServiceBookingLineForm[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [newCustomerAddress, setNewCustomerAddress] = useState('');
  const [newBookingMode, setNewBookingMode] = useState<BookingMode>('on_demand');
  const [newScheduledAt, setNewScheduledAt] = useState('');
  const [newNotes, setNewNotes] = useState<string>('');
  const [advanceAmount, setAdvanceAmount] = useState<string>('');
  const [advanceMode, setAdvanceMode] = useState<'cash' | 'online'>('cash');
  const [newProfessionalId, setNewProfessionalId] = useState('');
  const [creatingBooking, setCreatingBooking] = useState(false);

  // Units list & map
  const [units, setUnits] = useState<Array<{ id: string; unit_name: string; unit_symbol?: string | null }>>([]);
  const unitMap = useMemo(() => {
    const map = new Map<string, string>();
    units.forEach(u => map.set(u.id, u.unit_symbol || u.unit_name));
    return map;
  }, [units]);

  // Drawer advance and line states
  const [selectedBookingLines, setSelectedBookingLines] = useState<any[]>([]);
  const [showBillPrintBookingId, setShowBillPrintBookingId] = useState<string | null>(null);
  const [loadingSelectedLines, setLoadingSelectedLines] = useState(false);
  const [showAddAdvanceModal, setShowAddAdvanceModal] = useState(false);
  const [addAdvanceAmount, setAddAdvanceAmount] = useState('');
  const [addAdvanceMode, setAddAdvanceMode] = useState<'cash' | 'online'>('cash');
  const [submittingAdvance, setSubmittingAdvance] = useState(false);
  const [showRefundAdvanceModal, setShowRefundAdvanceModal] = useState(false);
  const [refundAdvanceMode, setRefundAdvanceMode] = useState<'cash' | 'online'>('cash');
  const [submittingRefund, setSubmittingRefund] = useState(false);

  // Payment link and messages state for drawer
  const [paymentLinkData, setPaymentLinkData] = useState<{
    url: string | null;
    status: 'created' | 'paid' | 'expired' | null;
    amount: number | null;
    expiresAt: string | null;
    reused?: boolean;
    messages?: any[];
  }>({
    url: null,
    status: null,
    amount: null,
    expiresAt: null,
    reused: false,
    messages: []
  });
  const [generatingLink, setGeneratingLink] = useState(false);

  const [createdSuccessBooking, setCreatedSuccessBooking] = useState<{
    id: string;
    bookingNumber: string;
    customerName: string;
    customerPhone?: string | null;
    chargeRemaining: number;
  } | null>(null);
  const [creatingAndSendingWhatsApp, setCreatingAndSendingWhatsApp] = useState(false);
  // Task 1 & 6: Office-managed jobs state
  const [showOfficeModal, setShowOfficeModal] = useState<{
    action: 'start' | 'complete';
    targetBooking: ServiceBooking;
  } | null>(null);
  const [officeOtpInput, setOfficeOtpInput] = useState('');
  const [officeReasonSelect, setOfficeReasonSelect] = useState('Professional has no smartphone');
  const [officeReasonCustom, setOfficeReasonCustom] = useState('');
  const [officeFinalPrice, setOfficeFinalPrice] = useState('');
  const [officeActionLoading, setOfficeActionLoading] = useState(false);
  const [selectedBookingHasOfficeEvent, setSelectedBookingHasOfficeEvent] = useState(false);

  // Task 3: Payment Visibility & Settlements (RPC: get_booking_payments)
  const [bookingPayments, setBookingPayments] = useState<{
    booking_number?: string;
    payment_status?: string;
    payment_mode?: string;
    advance_amount?: number;
    advance_mode?: string | null;
    advance_received_at?: string | null;
    online?: Array<{
      payment_id: string;
      purpose?: string;
      gross: number;
      fee: number;
      platform_commission: number;
      merchant_share: number;
      professional_share: number;
      settlement_status: string;
      at: string;
    }>;
    online_total?: number;
  } | null>(null);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const fetchBookingPayments = useCallback(async (bookingId: string) => {
    if (!supabase || !bookingId) return;
    setLoadingPayments(true);
    try {
      const { data, error } = await supabase.rpc('get_booking_payments', {
        p_booking_id: bookingId
      });
      if (error) {
        console.warn('get_booking_payments error:', error);
      } else if (data) {
        setBookingPayments(data);
      }
    } catch (err: any) {
      console.error('fetchBookingPayments exception:', err);
    } finally {
      setLoadingPayments(false);
    }
  }, [supabase]);

  useEffect(() => {
    if (selectedBooking?.id) {
      fetchBookingPayments(selectedBooking.id);
    } else {
      setBookingPayments(null);
    }
  }, [selectedBooking?.id, fetchBookingPayments]);

  // Check if selected booking has office events
  useEffect(() => {
    if (!selectedBooking?.id || !supabase) {
      setSelectedBookingHasOfficeEvent(false);
      return;
    }
    supabase.rpc('get_booking_events', { p_booking_id: selectedBooking.id })
      .then(({ data }: any) => {
        const hasOffice = (data || []).some((ev: any) => ev.via === 'office');
        setSelectedBookingHasOfficeEvent(hasOffice);
      })
      .catch(() => setSelectedBookingHasOfficeEvent(false));
  }, [selectedBooking?.id, supabase]);

  // Resolve branchId
  useEffect(() => {
    if (branchId) {
      setResolvedBranchId(branchId);
    } else if (activeBranchId) {
      setResolvedBranchId(activeBranchId);
    } else if (supabase && currentTenantId) {
      supabase
        .from('branches')
        .select('id')
        .eq('tenant_id', currentTenantId)
        .limit(1)
        .maybeSingle()
        .then(({ data }: { data: any }) => {
          if (data?.id) setResolvedBranchId(data.id);
        });
    }
  }, [branchId, activeBranchId, supabase, currentTenantId]);

  // Load all master data
  const loadData = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      // 1. Fetch catalog items
      const { data: catData, error: catErr } = await supabase
        .from('service_catalog_items')
        .select('id, service_name, base_price, category_id, is_active, allow_custom_price, unit_id')
        .eq('tenant_id', currentTenantId)
        .eq('is_active', true)
        .order('service_name', { ascending: true });
      if (catErr) throw catErr;
      setCatalogItems(catData || []);

      // 1b. Fetch units
      const { data: uData } = await supabase
        .from('units')
        .select('id, unit_name, unit_symbol');
      setUnits(uData || []);

      // 2. Fetch active professionals
      const { data: proData, error: proErr } = await supabase
        .from('service_professionals')
        .select('id, full_name, phone, is_active')
        .eq('tenant_id', currentTenantId)
        .eq('is_active', true)
        .order('full_name', { ascending: true });
      if (proErr) throw proErr;
      setProfessionals(proData || []);

      // 3. Fetch service bookings with service_booking_lines (newest first)
      const { data: bookData, error: bookErr } = await supabase
        .from('service_bookings')
        .select('*, service_booking_lines(id, catalog_item_id, item_name, quantity, line_total, notes)')
        .eq('tenant_id', currentTenantId)
        .order('created_at', { ascending: false });
      if (bookErr) throw bookErr;
      setBookings(bookData || []);

      // If a booking is currently selected in drawer, update its data as well
      setSelectedBooking(prev => {
        if (!prev) return null;
        const updated = ((bookData as ServiceBooking[]) || []).find((b: ServiceBooking) => b.id === prev.id);
        return updated || null;
      });
    } catch (err: any) {
      console.error('Error loading service bookings:', err);
      toast.error('Failed to load bookings: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load service lines whenever selectedBooking changes
  useEffect(() => {
    if (selectedBooking?.id && supabase) {
      setLoadingSelectedLines(true);
      supabase.rpc('get_service_booking_lines', { p_booking_id: selectedBooking.id })
        .then(({ data, error }: { data: any, error: any }) => {
          if (!error && data && Array.isArray(data)) {
            setSelectedBookingLines(data);
          } else if (selectedBooking.service_booking_lines && selectedBooking.service_booking_lines.length > 0) {
            setSelectedBookingLines(selectedBooking.service_booking_lines);
          } else {
            setSelectedBookingLines([]);
          }
        })
        .finally(() => setLoadingSelectedLines(false));
    } else {
      setSelectedBookingLines([]);
    }
  }, [selectedBooking?.id, supabase]);

  // Fetch messages and payment link for selected booking
  useEffect(() => {
    if (!selectedBooking?.id || !supabase) {
      setPaymentLinkData({
        url: null,
        status: null,
        amount: null,
        expiresAt: null,
        reused: false,
        messages: []
      });
      return;
    }

    const fetchBookingMessages = async () => {
      try {
        const { data: res } = await supabase.rpc('get_service_booking_messages', {
          p_booking_id: selectedBooking.id
        });
        if (res) {
          setPaymentLinkData({
            url: res.payment_link_url || selectedBooking.razorpay_payment_link_short_url || null,
            status: res.payment_link_status || (selectedBooking.razorpay_payment_link_status as any) || null,
            amount: res.charge_remaining ?? null,
            expiresAt: res.payment_link_expires_at || null,
            messages: res.messages || []
          });
        }
      } catch (err) {
        console.error('Error fetching booking messages:', err);
      }
    };

    fetchBookingMessages();
  }, [selectedBooking?.id, selectedBooking?.razorpay_payment_link_short_url, selectedBooking?.razorpay_payment_link_status, supabase]);

  // Task 1: While drawer is open and a link exists with status 'created', poll get_service_booking_messages every 5 seconds
  useEffect(() => {
    if (!selectedBooking?.id || !supabase) return;

    const chargeDue = Number(selectedBooking.booking_charge_due || 0);
    const advAmt = Number(selectedBooking.advance_amount || 0);
    const chargeRem = selectedBooking.booking_charge_waived ? 0 : Math.max(0, chargeDue - advAmt);

    if (paymentLinkData.status !== 'created' || chargeRem <= 0) return;

    const interval = setInterval(async () => {
      try {
        const { data: res } = await supabase.rpc('get_service_booking_messages', {
          p_booking_id: selectedBooking.id
        });
        if (res) {
          if (res.charge_remaining === 0 || res.payment_link_status === 'paid' || res.booking_confirmed) {
            toast.success('Payment received - booking confirmed');
            await loadData();
            const { data: updatedB } = await supabase
              .from('service_bookings')
              .select('*')
              .eq('id', selectedBooking.id)
              .maybeSingle();
            if (updatedB) {
              setSelectedBooking(updatedB);
            }
            setPaymentLinkData(prev => ({
              ...prev,
              status: 'paid',
              amount: 0
            }));
            clearInterval(interval);
          } else if (res.payment_link_status === 'expired') {
            setPaymentLinkData(prev => ({
              ...prev,
              status: 'expired'
            }));
            clearInterval(interval);
          }
        }
      } catch (pollErr) {
        console.error('Polling error:', pollErr);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedBooking?.id, paymentLinkData.status, selectedBooking?.booking_charge_due, selectedBooking?.advance_amount, selectedBooking?.booking_charge_waived, supabase, loadData]);

  // Create payment link handler via create-booking-charge-link edge function
  const handleCreatePaymentLink = async () => {
    if (!selectedBooking?.id || !supabase) return;
    setGeneratingLink(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-booking-charge-link', {
        body: { booking_id: selectedBooking.id }
      });

      if (error) {
        let errorMsg = error.message || 'Failed to create payment link';
        try {
          const errBody = await (error as any).context?.json?.();
          if (errBody?.error) errorMsg = errBody.error;
          else if (errBody?.message) errorMsg = errBody.message;
        } catch {}
        toast.error(errorMsg);
        return;
      }

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success(data?.reused ? 'Reused active payment link' : 'Payment link & QR created successfully!');

      setPaymentLinkData(prev => ({
        ...prev,
        url: data.short_url,
        status: 'created',
        amount: data.amount,
        expiresAt: data.expires_at,
        reused: Boolean(data.reused)
      }));

      // Re-fetch messages
      const { data: msgRes } = await supabase.rpc('get_service_booking_messages', {
        p_booking_id: selectedBooking.id
      });
      if (msgRes?.messages) {
        setPaymentLinkData(prev => ({ ...prev, messages: msgRes.messages }));
      }
    } catch (err: any) {
      console.error('Create link exception:', err);
      toast.error(err.message || 'Error creating payment link');
    } finally {
      setGeneratingLink(false);
    }
  };

  // Send payment request on WhatsApp
  const handleSendPaymentLinkWhatsApp = async () => {
    if (!selectedBooking || !supabase) return;
    try {
      const { data: msgRes } = await supabase.rpc('get_service_booking_messages', {
        p_booking_id: selectedBooking.id
      });

      const payMsg = msgRes?.messages?.find((m: any) => m.key === 'payment_request') || msgRes?.messages?.[0];
      const linkUrl = msgRes?.payment_link_url || paymentLinkData.url;
      let textToSend = payMsg?.message;

      const chargeDue = Number(selectedBooking.booking_charge_due || 0);
      const advAmt = Number(selectedBooking.advance_amount || 0);
      const chargeRem = Math.max(0, chargeDue - advAmt);

      if (!textToSend && linkUrl) {
        textToSend = `Namaste ${selectedBooking.customer_name}, please pay minimum booking advance ₹${chargeRem.toFixed(2)} to confirm booking #${selectedBooking.booking_number}: ${linkUrl}`;
      }

      const phone = selectedBooking.customer_phone || payMsg?.phone || '';
      let cleanPhone = phone.replace(/[^0-9]/g, '');
      if (cleanPhone.length === 10) cleanPhone = '91' + cleanPhone;

      const waUrl = cleanPhone 
        ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(textToSend || '')}`
        : `https://api.whatsapp.com/send?text=${encodeURIComponent(textToSend || '')}`;
      
      window.open(waUrl, '_blank');
      toast.success('Opening WhatsApp with payment link');
    } catch (err: any) {
      console.error('WhatsApp send error:', err);
      toast.error(err.message || 'Failed to send WhatsApp message');
    }
  };

  const copyPaymentLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Payment link copied to clipboard!');
  };

  // Task 2: Create link and send on WhatsApp for newly created booking
  const handleCreateAndSendWhatsAppForNewBooking = async () => {
    if (!createdSuccessBooking?.id || !supabase) return;
    setCreatingAndSendingWhatsApp(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-booking-charge-link', {
        body: { booking_id: createdSuccessBooking.id }
      });

      if (error) {
        let errorMsg = error.message || 'Failed to create payment link';
        try {
          const errBody = await (error as any).context?.json?.();
          if (errBody?.error) errorMsg = errBody.error;
          else if (errBody?.message) errorMsg = errBody.message;
        } catch {}
        toast.error(errorMsg);
        return;
      }

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success('Payment link generated! Opening WhatsApp...');

      const { data: msgRes } = await supabase.rpc('get_service_booking_messages', {
        p_booking_id: createdSuccessBooking.id
      });

      const payMsg = msgRes?.messages?.find((m: any) => m.key === 'payment_request') || msgRes?.messages?.[0];
      let textToSend = payMsg?.message;
      if (!textToSend && data?.short_url) {
        textToSend = `Namaste ${createdSuccessBooking.customerName}, please pay minimum booking advance ₹${createdSuccessBooking.chargeRemaining.toFixed(2)} to confirm booking #${createdSuccessBooking.bookingNumber}: ${data.short_url}`;
      }

      const phone = createdSuccessBooking.customerPhone || payMsg?.phone || '';
      let cleanPhone = phone.replace(/[^0-9]/g, '');
      if (cleanPhone.length === 10) cleanPhone = '91' + cleanPhone;

      const waUrl = cleanPhone 
        ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(textToSend || '')}`
        : `https://api.whatsapp.com/send?text=${encodeURIComponent(textToSend || '')}`;
      
      window.open(waUrl, '_blank');
      setCreatedSuccessBooking(null);
    } catch (err: any) {
      console.error('Error creating and sending WhatsApp link:', err);
      toast.error(err.message || 'Failed to send payment link on WhatsApp');
    } finally {
      setCreatingAndSendingWhatsApp(false);
    }
  };

  // Fast client-side lookups
  const catalogLookup = useMemo(() => {
    const map = new Map<string, CatalogItem>();
    catalogItems.forEach(item => map.set(item.id, item));
    return map;
  }, [catalogItems]);

  const professionalLookup = useMemo(() => {
    const map = new Map<string, ProfessionalItem>();
    professionals.forEach(pro => map.set(pro.id, pro));
    return map;
  }, [professionals]);

  // Filtered Bookings
  const filteredBookings = useMemo(() => {
    return bookings.filter(b => {
      // Status filter
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;

      // Mode filter
      if (modeFilter !== 'all' && b.booking_mode !== modeFilter) return false;

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const serviceName = catalogLookup.get(b.catalog_item_id)?.service_name?.toLowerCase() || '';
        const proName = b.professional_id ? professionalLookup.get(b.professional_id)?.full_name?.toLowerCase() || '' : '';
        const bookingNum = b.booking_number?.toLowerCase() || '';
        const custName = b.customer_name?.toLowerCase() || '';
        const custPhone = b.customer_phone?.toLowerCase() || '';

        const matches = 
          bookingNum.includes(q) ||
          custName.includes(q) ||
          custPhone.includes(q) ||
          serviceName.includes(q) ||
          proName.includes(q);

        if (!matches) return false;
      }

      return true;
    });
  }, [bookings, statusFilter, modeFilter, searchQuery, catalogLookup, professionalLookup]);

  // Summary counts
  const totalCount = bookings.length;
  const requestedCount = bookings.filter(b => b.status === 'requested').length;
  const activeCount = bookings.filter(b => ['assigned', 'accepted', 'in_progress'].includes(b.status)).length;
  const completedCount = bookings.filter(b => b.status === 'completed').length;

  // Status Badge Helper
  const getStatusBadge = (status: BookingStatus) => {
    switch (status) {
      case 'requested':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            Requested
          </span>
        );
      case 'assigned':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
            Assigned
          </span>
        );
      case 'accepted':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
            Accepted
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-ping" />
            In Progress
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
            <CheckCircle2 size={12} />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
            <XCircle size={12} />
            Cancelled
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
            Rejected
          </span>
        );
      case 'no_show':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
            No Show
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
            {status}
          </span>
        );
    }
  };

  // ==========================================
  // Action Handlers (State Machine & RPC Calls)
  // ==========================================

  // 1. Assign Professional
  const handleAssignProfessional = async () => {
    if (!selectedBooking || !assignProId || !supabase) return;
    setActionLoading(true);

    try {
      const { error } = await supabase.rpc('assign_service_booking', {
        p_booking_id: selectedBooking.id,
        p_professional_id: assignProId
      });

      if (error) throw error;

      toast.success('Professional assigned successfully.');
      setAssignProId('');
      await loadData();
    } catch (err: any) {
      console.error('Assign professional error:', err);
      toast.error(err.message || 'Failed to assign professional');
    } finally {
      setActionLoading(false);
    }
  };

  // 2. Mark Accepted / Rejected
  const handleStatusTransition = async (newStatus: 'accepted' | 'rejected' | 'no_show') => {
    if (!selectedBooking || !supabase) return;
    setActionLoading(true);

    try {
      const { error } = await supabase.rpc('update_service_booking_status', {
        p_booking_id: selectedBooking.id,
        p_new_status: newStatus,
        p_otp: null,
        p_cancelled_reason: null,
        p_final_price: null
      });

      if (error) throw error;

      toast.success(`Booking marked as ${newStatus}.`);
      await loadData();
    } catch (err: any) {
      console.error(`Transition to ${newStatus} error:`, err);
      toast.error(err.message || `Failed to update booking to ${newStatus}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Task 1: Office Update Service Booking Handlers
  const handleOfficeMarkAccepted = async () => {
    if (!selectedBooking || !supabase) return;
    setOfficeActionLoading(true);
    try {
      const { data, error } = await supabase.rpc('office_update_service_booking', {
        p_booking_id: selectedBooking.id,
        p_new_status: 'accepted',
        p_otp: null,
        p_override_reason: 'Office accepted',
        p_final_price: null,
        p_cancelled_reason: null
      });

      if (error) {
        toast.error(error.message || 'Failed to accept booking');
        return;
      }

      toast.success('Booking marked accepted from office.');
      await loadData();
    } catch (err: any) {
      console.error('Office mark accepted error:', err);
      toast.error(err.message || 'Failed to accept booking');
    } finally {
      setOfficeActionLoading(false);
    }
  };

  const handleOfficeActionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showOfficeModal || !supabase) return;
    const { action, targetBooking } = showOfficeModal;
    const newStatus = action === 'start' ? 'in_progress' : 'completed';

    const otp = officeOtpInput.trim();
    // Task 1: OTP and reason are now OPTIONAL. If empty, defaults to "Managed from office".
    let overrideReason: string | null = null;
    if (!otp) {
      const selected = officeReasonSelect === 'Other' ? officeReasonCustom.trim() : officeReasonSelect;
      overrideReason = selected || 'Managed from office';
    }

    let finalPriceNum: number | null = null;
    if (action === 'complete') {
      finalPriceNum = officeFinalPrice ? parseFloat(officeFinalPrice) : Number(targetBooking.quoted_price || 0);
      if (isNaN(finalPriceNum) || finalPriceNum < 0) {
        toast.error('Please enter a valid final price');
        return;
      }
    }

    setOfficeActionLoading(true);
    try {
      const { data, error } = await supabase.rpc('office_update_service_booking', {
        p_booking_id: targetBooking.id,
        p_new_status: newStatus,
        p_otp: otp || null,
        p_override_reason: otp ? null : overrideReason,
        p_final_price: finalPriceNum,
        p_cancelled_reason: null
      });

      if (error) {
        toast.error(error.message || `Failed to update status to ${newStatus}`);
        return;
      }

      setShowOfficeModal(null);
      setOfficeOtpInput('');
      setOfficeReasonCustom('');
      await loadData();

      // If completed, backend AUTO-GENERATES the bill (invoice_id)
      if (action === 'complete') {
        toast.success('Service completed & Bill auto-generated!');
        setShowBillPrintBookingId(targetBooking.id);
      } else {
        toast.success('Work started from office!');
      }

      if (selectedBooking?.id === targetBooking.id) {
        fetchBookingPayments(targetBooking.id);
      }
    } catch (err: any) {
      console.error('Office action submit error:', err);
      toast.error(err.message || 'Operation failed');
    } finally {
      setOfficeActionLoading(false);
    }
  };

  // 3. Start Service (validates start_otp)
  const handleStartService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBooking || !supabase) return;

    if (!startOtpInput.trim()) {
      toast.error('Please enter the start verification OTP');
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('update_service_booking_status', {
        p_booking_id: selectedBooking.id,
        p_new_status: 'in_progress',
        p_otp: startOtpInput.trim(),
        p_cancelled_reason: null,
        p_final_price: null
      });

      if (error) throw error;

      toast.success('Service started successfully!');
      setShowStartOtpModal(false);
      setStartOtpInput('');
      await loadData();
    } catch (err: any) {
      console.error('Start service error:', err);
      // Show exact Postgres/RPC error message (e.g. "Incorrect or missing start OTP.")
      toast.error(err.message || 'Incorrect start OTP. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // 4. Complete Service (validates complete_otp and final_price)
  const handleCompleteService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBooking || !supabase) return;

    if (!completeOtpInput.trim()) {
      toast.error('Please enter the completion verification OTP');
      return;
    }

    setActionLoading(true);
    try {
      const finalPrice = finalPriceInput ? Number(finalPriceInput) : null;

      const { error } = await supabase.rpc('update_service_booking_status', {
        p_booking_id: selectedBooking.id,
        p_new_status: 'completed',
        p_otp: completeOtpInput.trim(),
        p_cancelled_reason: null,
        p_final_price: finalPrice
      });

      if (error) throw error;

      toast.success('Service marked as completed!');
      setShowCompleteModal(false);
      setCompleteOtpInput('');
      setFinalPriceInput('');
      await loadData();
    } catch (err: any) {
      console.error('Complete service error:', err);
      // Show exact backend error message
      toast.error(err.message || 'Incorrect completion OTP. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // 5. Cancel Booking (with reason)
  const handleCancelBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBooking || !supabase) return;

    if (!cancelReasonInput.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase.rpc('office_update_service_booking', {
        p_booking_id: selectedBooking.id,
        p_new_status: 'cancelled',
        p_otp: null,
        p_override_reason: null,
        p_final_price: null,
        p_cancelled_reason: cancelReasonInput.trim()
      });

      if (error) throw error;

      toast.success('Booking cancelled.');
      setShowCancelModal(false);
      setCancelReasonInput('');
      await loadData();
    } catch (err: any) {
      console.error('Cancel booking error:', err);
      toast.error(err.message || 'Failed to cancel booking');
    } finally {
      setActionLoading(false);
    }
  };

  // ==========================================
  // Create New Booking Handler V2
  // ==========================================
  const handleOpenNewBooking = () => {
    const firstItem = catalogItems[0];
    const initialLine: ServiceBookingLineForm = {
      id: Math.random().toString(36).substring(2, 9),
      catalog_item_id: firstItem?.id || '',
      quantity: '',
      custom_total: '',
      notes: ''
    };
    setServiceLines([initialLine]);
    setSelectedCustomerId(null);
    setNewCustomerName('');
    setNewCustomerPhone('');
    setNewCustomerAddress('');
    setNewBookingMode('on_demand');
    setNewScheduledAt('');
    setNewNotes('');
    setAdvanceAmount('');
    setAdvanceMode('cash');
    setNewProfessionalId('');
    setShowNewBookingModal(true);
  };

  const handleAddServiceLine = () => {
    if (serviceLines.length >= 20) {
      toast.error('Maximum 20 services allowed in a single booking');
      return;
    }
    const firstItem = catalogItems[0];
    const newLine: ServiceBookingLineForm = {
      id: Math.random().toString(36).substring(2, 9),
      catalog_item_id: firstItem?.id || '',
      quantity: '',
      custom_total: '',
      notes: ''
    };
    setServiceLines(prev => [...prev, newLine]);
  };

  const handleRemoveServiceLine = (idToRemove: string) => {
    if (serviceLines.length <= 1) {
      toast.error('At least 1 service is required');
      return;
    }
    setServiceLines(prev => prev.filter(l => l.id !== idToRemove));
  };

  const handleLineChange = (id: string, field: keyof ServiceBookingLineForm, value: string) => {
    setServiceLines(prev => prev.map(line => {
      if (line.id !== id) return line;
      if (field === 'quantity') {
        // regex check: only allow digits with optional single dot
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
          return { ...line, quantity: value };
        }
        return line;
      }
      return { ...line, [field]: value };
    }));
  };

  // Calculate live Grand Total of all lines
  const calculatedGrandTotal = useMemo(() => {
    let sum = 0;
    serviceLines.forEach(l => {
      const item = catalogLookup.get(l.catalog_item_id);
      if (item?.allow_custom_price) {
        sum += parseFloat(l.custom_total) || 0;
      } else if (item) {
        const qty = parseFloat(l.quantity) || 0;
        sum += (item.base_price ?? 0) * qty;
      }
    });
    return parseFloat(sum.toFixed(2));
  }, [serviceLines, catalogLookup]);

  const handleCreateBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId) return;

    if (!newCustomerName.trim()) {
      toast.error('Customer name daalein');
      return;
    }

    if (serviceLines.length === 0) {
      toast.error('Kam se kam ek service chunein');
      return;
    }

    // Validate each service line
    const parsedLines: Array<{
      catalog_item_id: string;
      quantity: number;
      total_price: number;
      notes: string;
    }> = [];

    for (let i = 0; i < serviceLines.length; i++) {
      const line = serviceLines[i];
      if (!line.catalog_item_id) {
        toast.error(`Service #${i + 1} select karein`);
        return;
      }
      const item = catalogLookup.get(line.catalog_item_id);
      const qtyNum = parseFloat(line.quantity);
      if (isNaN(qtyNum) || qtyNum <= 0) {
        toast.error(`Service #${i + 1} ki quantity daalein (0 se zyada)`);
        return;
      }
      if (qtyNum > 1000) {
        toast.error(`Service #${i + 1} ki quantity 1000 se zyada nahi ho sakti`);
        return;
      }

      let lineTotal = 0;
      if (item?.allow_custom_price) {
        const customPriceNum = parseFloat(line.custom_total);
        if (isNaN(customPriceNum) || customPriceNum < 0) {
          toast.error(`Service #${i + 1} ka custom price daalein`);
          return;
        }
        lineTotal = parseFloat(customPriceNum.toFixed(2));
      } else {
        lineTotal = parseFloat(((item?.base_price ?? 0) * qtyNum).toFixed(2));
      }

      parsedLines.push({
        catalog_item_id: line.catalog_item_id,
        quantity: qtyNum,
        total_price: lineTotal,
        notes: line.notes.trim()
      });
    }

    // Validate Advance Amount if provided
    const advNum = advanceAmount ? parseFloat(advanceAmount) : 0;
    if (advNum < 0) {
      toast.error('Advance amount valid daalein');
      return;
    }
    if (advNum > calculatedGrandTotal && calculatedGrandTotal > 0) {
      const proceed = window.confirm(
        `Advance (₹${advNum}) total quoted price (₹${calculatedGrandTotal}) se zyada hai. Kya aap continue karna chahte hain?`
      );
      if (!proceed) return;
    }

    setCreatingBooking(true);
    try {
      // Resolve valid branch
      let branchToUse = resolvedBranchId;
      if (!branchToUse) {
        const { data: bData } = await supabase
          .from('branches')
          .select('id')
          .eq('tenant_id', currentTenantId)
          .limit(1)
          .maybeSingle();
        branchToUse = bData?.id || null;
      }

      if (!branchToUse) {
        throw new Error('No active branch found. Please configure a branch first.');
      }

      const scheduledAtIso = 
        newBookingMode === 'scheduled' && newScheduledAt 
          ? new Date(newScheduledAt).toISOString() 
          : null;

      // Always call create_service_booking_v2
      const { data: createdBookingId, error: createErr } = await supabase.rpc('create_service_booking_v2', {
        p_tenant_id: currentTenantId,
        p_branch_id: branchToUse,
        p_customer_name: newCustomerName.trim(),
        p_lines: parsedLines,
        p_customer_phone: newCustomerPhone.trim() || null,
        p_customer_address: newCustomerAddress.trim() || null,
        p_scheduled_at: scheduledAtIso,
        p_booking_mode: newBookingMode,
        p_professional_id: newProfessionalId || null,
        p_notes: newNotes.trim() || null,
        p_customer_id: selectedCustomerId || null,
        p_advance_amount: advNum || 0,
        p_advance_mode: advanceMode || 'cash'
      });

      if (createErr) throw createErr;

      let chargeRem = 0;
      let newBRow: any = null;
      if (createdBookingId && supabase) {
        const { data: bData } = await supabase
          .from('service_bookings')
          .select('id, booking_number, customer_name, customer_phone, booking_charge_due, advance_amount, booking_charge_waived')
          .eq('id', createdBookingId)
          .maybeSingle();
        if (bData) {
          newBRow = bData;
          const due = Number(bData.booking_charge_due || 0);
          const adv = Number(bData.advance_amount || 0);
          chargeRem = bData.booking_charge_waived ? 0 : Math.max(0, due - adv);
        }
      }

      setCreatedSuccessBooking({
        id: createdBookingId,
        bookingNumber: newBRow?.booking_number || 'New Booking',
        customerName: newBRow?.customer_name || newCustomerName,
        customerPhone: newBRow?.customer_phone || newCustomerPhone,
        chargeRemaining: chargeRem
      });

      setShowNewBookingModal(false);
      await loadData();
    } catch (err: any) {
      console.error('Create booking error:', err);
      toast.error(err.message || 'Failed to create booking');
    } finally {
      setCreatingBooking(false);
    }
  };

  // Record Additional Advance Handler (Task 4)
  const handleRecordAdvance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBooking || !supabase) return;
    const amountNum = parseFloat(addAdvanceAmount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast.error('Valid advance amount daalein');
      return;
    }
    setSubmittingAdvance(true);
    try {
      const { error } = await supabase.rpc('record_service_advance', {
        p_booking_id: selectedBooking.id,
        p_amount: amountNum,
        p_mode: addAdvanceMode
      });
      if (error) throw error;

      // Re-fetch messages for payment_received confirmation
      const { data: msgRes } = await supabase.rpc('get_service_booking_messages', {
        p_booking_id: selectedBooking.id
      });
      const payRecvMsg = msgRes?.messages?.find((m: any) => m.key === 'payment_received');
      const isConfirmed = msgRes?.booking_confirmed || (msgRes?.charge_remaining === 0);

      toast.success(
        isConfirmed
          ? `Advance ₹${amountNum} recorded! Booking confirmed.`
          : `Advance ₹${amountNum} recorded successfully!`,
        payRecvMsg?.message && payRecvMsg.enabled ? {
          action: {
            label: 'Send on WhatsApp',
            onClick: () => {
              const phone = (selectedBooking.customer_phone || payRecvMsg.phone || '').replace(/\D/g, '');
              const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(payRecvMsg.message)}`;
              window.open(waUrl, '_blank');
              supabase.rpc('log_service_booking_event', {
                p_booking_id: selectedBooking.id,
                p_event: 'msg_payment_received',
                p_note: 'Sent advance payment confirmation on WhatsApp'
              });
            }
          }
        } : undefined
      );

      setShowAddAdvanceModal(false);
      setAddAdvanceAmount('');
      await loadData();
    } catch (err: any) {
      console.error('Record advance error:', err);
      toast.error(err.message || 'Advance record karne me error');
    } finally {
      setSubmittingAdvance(false);
    }
  };

  // Refund Advance Handler
  const handleRefundAdvance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBooking || !supabase) return;
    const advRem = (Number(selectedBooking.advance_amount) || 0) 
      - (Number(selectedBooking.advance_applied) || 0) 
      - (Number(selectedBooking.advance_refunded) || 0);

    const proceed = window.confirm(`Kya aap baaki bacha advance ₹${advRem} refund karna chahte hain?`);
    if (!proceed) return;

    setSubmittingRefund(true);
    try {
      const { error } = await supabase.rpc('refund_service_advance', {
        p_booking_id: selectedBooking.id,
        p_amount: null,
        p_mode: refundAdvanceMode
      });
      if (error) throw error;
      toast.success('Advance refund processed successfully!');
      setShowRefundAdvanceModal(false);
      await loadData();
    } catch (err: any) {
      console.error('Refund advance error:', err);
      toast.error(err.message || 'Advance refund karne me error');
    } finally {
      setSubmittingRefund(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  return (
    <div className="space-y-6">
      {/* Top Banner / Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-xl bg-primary/10 text-primary">
              <Calendar size={22} />
            </span>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Booking & Appointments</h1>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Dispatch, manage, and verify on-demand and scheduled service bookings with secure OTP completion.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={loadData}
            variant="outline"
            className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300"
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>

          <Button
            onClick={handleOpenNewBooking}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-medium px-4 py-2 rounded-xl shadow-sm transition-all"
          >
            <Plus size={16} />
            <span>New Booking</span>
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Bookings</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{totalCount}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">New Requested</p>
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">{requestedCount}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Active In-Flight</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">{activeCount}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Completed</p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">{completedCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters Bar */}
      <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="relative w-full md:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search booking #, customer, service..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 h-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg text-sm"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Status Filter */}
          <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
            <Filter size={14} className="text-slate-400" />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="h-10 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="all">All Statuses</option>
              <option value="requested">Requested</option>
              <option value="assigned">Assigned</option>
              <option value="accepted">Accepted</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
              <option value="no_show">No Show</option>
            </select>
          </div>

          {/* Mode Filter */}
          <select
            value={modeFilter}
            onChange={e => setModeFilter(e.target.value)}
            className="h-10 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-700 dark:text-slate-200 outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="all">All Modes</option>
            <option value="on_demand">On-Demand</option>
            <option value="scheduled">Scheduled</option>
          </select>
        </div>
      </div>

      {/* Bookings Table View */}
      {loading ? (
        <div className="p-16 flex flex-col items-center justify-center text-slate-400">
          <Loader2 size={32} className="animate-spin mb-3 text-primary" />
          <p className="text-sm">Loading bookings and schedule...</p>
        </div>
      ) : filteredBookings.length === 0 ? (
        <Card className="border border-dashed border-slate-300 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 p-12 text-center rounded-2xl">
          <div className="flex flex-col items-center justify-center max-w-sm mx-auto">
            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 mb-4">
              <Calendar size={24} />
            </div>
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-200 mb-1">
              No service bookings found
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-6">
              {searchQuery || statusFilter !== 'all' || modeFilter !== 'all'
                ? 'Try adjusting your search criteria or filters.'
                : 'Create your first service booking manually or await incoming requests.'}
            </p>
            <Button
              onClick={handleOpenNewBooking}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-4 py-2 rounded-xl"
            >
              <Plus size={16} />
              <span>Create New Booking</span>
            </Button>
          </div>
        </Card>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="px-5 py-3.5">Booking #</th>
                  <th className="px-5 py-3.5">Customer</th>
                  <th className="px-5 py-3.5">Service</th>
                  <th className="px-5 py-3.5">Assigned Pro</th>
                  <th className="px-5 py-3.5">Schedule / Mode</th>
                  <th className="px-5 py-3.5">Quoted Price</th>
                  <th className="px-5 py-3.5">Status</th>
                  <th className="px-5 py-3.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                {filteredBookings.map(b => {
                  const service = catalogLookup.get(b.catalog_item_id);
                  const professional = b.professional_id ? professionalLookup.get(b.professional_id) : null;
                  const isSelected = selectedBooking?.id === b.id;

                  return (
                    <tr
                      key={b.id}
                      onClick={() => setSelectedBooking(b)}
                      className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors ${
                        isSelected ? 'bg-primary/5 dark:bg-primary/10' : ''
                      }`}
                    >
                      {/* Booking Number */}
                      <td className="px-5 py-4 font-mono font-bold text-slate-900 dark:text-white">
                        <span className="flex items-center gap-1.5">
                          <span>{b.booking_number}</span>
                          {b.source === 'consumer_app' && (
                            <span className="text-[9px] px-1.5 py-0.2 rounded bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 font-sans font-medium">
                              App
                            </span>
                          )}
                        </span>
                      </td>

                      {/* Customer Name & Phone */}
                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-900 dark:text-white">
                          {b.customer_name}
                        </div>
                        {b.customer_phone && (
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1">
                            <Phone size={10} />
                            <span>{b.customer_phone}</span>
                          </div>
                        )}
                      </td>

                      {/* Service Name & Quantity / Notes */}
                      <td className="px-5 py-4">
                        <div className="font-medium text-slate-800 dark:text-slate-200 flex items-center flex-wrap gap-1">
                          <span>
                            {service?.service_name || (b.service_booking_lines && b.service_booking_lines[0]?.item_name) || 'Service Item'}
                          </span>
                          {b.service_booking_lines && b.service_booking_lines.length > 1 ? (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/10 text-primary border border-primary/20">
                              +{b.service_booking_lines.length - 1} more
                            </span>
                          ) : b.quantity && b.quantity > 1 ? (
                            <span className="ml-1 text-xs font-bold text-primary">x {b.quantity}</span>
                          ) : null}
                        </div>
                        {b.customer_notes && (
                          <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate max-w-xs mt-0.5 italic">
                            Note: {b.customer_notes}
                          </p>
                        )}
                      </td>

                      {/* Assigned Professional */}
                      <td className="px-5 py-4">
                        {professional ? (
                          <span className="inline-flex items-center gap-1.5 font-medium text-slate-800 dark:text-slate-200">
                            <UserCheck size={13} className="text-primary" />
                            <span>{professional.full_name}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                            Unassigned
                          </span>
                        )}
                      </td>

                      {/* Schedule / Mode */}
                      <td className="px-5 py-4">
                        {b.booking_mode === 'on_demand' ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
                            <Zap size={12} />
                            <span>On-demand</span>
                          </span>
                        ) : b.scheduled_at ? (
                          <span className="inline-flex items-center gap-1 text-slate-700 dark:text-slate-300">
                            <Clock size={12} className="text-slate-400" />
                            <span>{new Date(b.scheduled_at).toLocaleString()}</span>
                          </span>
                        ) : (
                          <span className="text-slate-400">Scheduled (TBD)</span>
                        )}
                      </td>

                      {/* Price */}
                      <td className="px-5 py-4 font-bold text-slate-900 dark:text-white">
                        ₹{b.quoted_price}
                        {b.final_price !== null && b.final_price !== undefined && b.final_price !== b.quoted_price && (
                          <span className="text-[10px] text-slate-400 font-normal block">
                            Final: ₹{b.final_price}
                          </span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1 items-start">
                          {getStatusBadge(b.status)}
                          {(() => {
                            const due = Number(b.booking_charge_due || 0);
                            const adv = Number(b.advance_amount || 0);
                            const isWaived = Boolean(b.booking_charge_waived);
                            if (due > 0 && !isWaived) {
                              if (adv < due) {
                                return (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                    Awaiting booking payment
                                  </span>
                                );
                              } else {
                                return (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
                                    <CheckCircle2 size={10} />
                                    Confirmed
                                  </span>
                                );
                              }
                            }
                            return null;
                          })()}
                        </div>
                      </td>

                      {/* View Action */}
                      <td className="px-5 py-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBooking(b);
                          }}
                          className="h-8 px-2 text-primary hover:bg-primary/10 rounded-lg text-xs font-semibold"
                        >
                          <span>Manage</span>
                          <ChevronRight size={14} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* Detail Slide-over Panel / Drawer                          */}
      {/* ========================================================= */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex justify-end animate-in fade-in duration-150">
          <div className="bg-white dark:bg-slate-900 w-full max-w-lg h-full shadow-2xl flex flex-col border-l border-slate-200 dark:border-slate-800 animate-in slide-in-from-right duration-200 overflow-hidden">
            {/* Drawer Header */}
            <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-bold text-slate-900 dark:text-white font-mono">
                    {selectedBooking.booking_number}
                  </h2>
                  {getStatusBadge(selectedBooking.status)}
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                  Created {new Date(selectedBooking.created_at).toLocaleString()}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {selectedBooking.invoice_id && (
                  <Button
                    size="sm"
                    onClick={() => setShowBillPrintBookingId(selectedBooking.id)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-2.5 font-semibold gap-1.5 rounded-lg shadow-xs"
                  >
                    <Printer size={13} />
                    <span>Print / Preview</span>
                  </Button>
                )}
                <button
                  onClick={() => setSelectedBooking(null)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Drawer Content */}
            <div className="overflow-y-auto flex-1 p-6 space-y-6">
              {/* Task 1: Pending Minimum Booking Amount Banner */}
              {(() => {
                const chargeDue = Number(selectedBooking.booking_charge_due || 0);
                const advAmt = Number(selectedBooking.advance_amount || 0);
                const isWaived = Boolean(selectedBooking.booking_charge_waived);
                const chargeRem = isWaived ? 0 : Math.max(0, chargeDue - advAmt);

                if (chargeRem <= 0 && !isWaived && chargeDue <= 0) return null;

                const linkStatus = paymentLinkData.status;
                const linkUrl = paymentLinkData.url || selectedBooking.booking_charge_link_url || selectedBooking.razorpay_payment_link_short_url;

                return (
                  <div className="p-4 rounded-xl border border-amber-300 dark:border-amber-800/80 bg-gradient-to-br from-amber-50 to-orange-50/60 dark:from-amber-950/40 dark:to-orange-950/20 space-y-3.5 shadow-xs">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400">
                          <IndianRupee size={18} />
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold tracking-wider text-amber-800 dark:text-amber-300">
                            Minimum Booking Amount
                          </span>
                          <div className="flex items-baseline gap-2">
                            <span className="text-xl font-extrabold font-mono text-slate-900 dark:text-white">
                              ₹{chargeRem.toFixed(2)}
                            </span>
                            <span className="text-[11px] text-slate-500 dark:text-slate-400">
                              (Due: ₹{chargeDue.toFixed(2)} | Paid: ₹{advAmt.toFixed(2)})
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Status Chip */}
                      <div>
                        {chargeRem === 0 ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
                            <CheckCircle2 size={12} />
                            Paid
                          </span>
                        ) : linkStatus === 'created' ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border border-sky-300 dark:border-sky-800">
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" />
                            Link created
                          </span>
                        ) : linkStatus === 'expired' ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-300 dark:border-rose-800">
                            <XCircle size={12} />
                            Expired
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                            Awaiting payment
                          </span>
                        )}
                      </div>
                    </div>

                    {/* If link created and still pending */}
                    {chargeRem > 0 && linkStatus === 'created' && linkUrl && (
                      <div className="bg-white dark:bg-slate-900 p-3.5 rounded-xl border border-amber-200 dark:border-amber-900/40 space-y-3">
                        <div className="flex flex-col sm:flex-row items-center gap-3.5">
                          <div className="bg-white p-2 rounded-lg border border-slate-200 dark:border-slate-800 shadow-xs shrink-0">
                            <QRCodeSVG value={linkUrl} size={110} level="M" />
                          </div>
                          <div className="flex-1 min-w-0 space-y-1.5 text-center sm:text-left">
                            <div className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                              Scan QR or use payment link
                            </div>
                            <div className="p-1.5 px-2 bg-slate-50 dark:bg-slate-800 rounded font-mono text-[11px] text-slate-700 dark:text-slate-300 truncate">
                              {linkUrl}
                            </div>
                            {paymentLinkData.expiresAt && (
                              <p className="text-[10px] text-slate-400">
                                Valid until: {new Date(paymentLinkData.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ({new Date(paymentLinkData.expiresAt).toLocaleDateString()})
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100 dark:border-slate-800">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => copyPaymentLink(linkUrl)}
                            variant="outline"
                            className="h-8 text-xs font-semibold gap-1.5 border-slate-300 dark:border-slate-700"
                          >
                            <Copy size={12} />
                            <span>Copy link</span>
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleSendPaymentLinkWhatsApp}
                            className="h-8 text-xs font-semibold gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
                          >
                            <Send size={12} />
                            <span>Send on WhatsApp</span>
                          </Button>
                          <a
                            href={linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary font-semibold hover:underline ml-auto"
                          >
                            <span>Open Link</span>
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons if charge remaining */}
                    {chargeRem > 0 && (
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        {(!linkUrl || linkStatus === 'expired') && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleCreatePaymentLink}
                            disabled={generatingLink}
                            className="h-8 text-xs font-semibold gap-1.5 bg-amber-600 hover:bg-amber-700 text-white shadow-xs"
                          >
                            {generatingLink ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <QrCode size={13} />
                            )}
                            <span>{linkStatus === 'expired' ? 'Create new link' : 'Create payment link / QR'}</span>
                          </Button>
                        )}

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setAddAdvanceAmount(chargeRem.toString());
                            setAddAdvanceMode('cash');
                            setShowAddAdvanceModal(true);
                          }}
                          className="h-8 text-xs font-semibold gap-1.5 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          <IndianRupee size={12} />
                          <span>Take cash / other payment</span>
                        </Button>
                      </div>
                    )}

                    <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                      Online payments come to the BahiBox platform account and are transferred to you after settlement.
                    </p>
                  </div>
                );
              })()}

              {/* Service & Pricing Card */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">Primary Service</span>
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white mt-0.5">
                      {catalogLookup.get(selectedBooking.catalog_item_id)?.service_name || 'Selected Service'}
                      {selectedBooking.quantity && selectedBooking.quantity > 1 ? ` x ${selectedBooking.quantity}` : ''}
                    </h3>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider block">Quoted Price</span>
                    <span className="text-base font-bold text-slate-900 dark:text-white">
                      ₹{selectedBooking.quoted_price}
                    </span>
                    {selectedBooking.final_price !== null && selectedBooking.final_price !== undefined && (
                      <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold block">
                        Final: ₹{selectedBooking.final_price}
                      </span>
                    )}
                  </div>
                </div>

                {/* Multiple Services Lines Breakdown */}
                {selectedBookingLines.length > 0 && (
                  <div className="pt-2 border-t border-slate-200 dark:border-slate-700 space-y-1.5">
                    <span className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block">
                      All Service Lines ({selectedBookingLines.length})
                    </span>
                    <div className="space-y-1 bg-white dark:bg-slate-900 rounded-lg p-2 border border-slate-200 dark:border-slate-800">
                      {selectedBookingLines.map((ln: any, idx: number) => (
                        <div key={ln.id || idx} className="flex items-center justify-between text-xs py-1 border-b last:border-b-0 border-slate-100 dark:border-slate-800">
                          <div>
                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                              {ln.item_name || catalogLookup.get(ln.catalog_item_id)?.service_name || 'Service'}
                            </span>
                            <span className="text-slate-400 ml-1.5 text-[11px]">x {ln.quantity}</span>
                            {ln.notes && (
                              <p className="text-[10px] text-slate-500 italic mt-0.5">{ln.notes}</p>
                            )}
                          </div>
                          <span className="font-mono font-bold text-slate-900 dark:text-white">
                            ₹{Number(ln.line_total || 0).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedBooking.customer_notes && (
                  <div className="p-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block mb-0.5">Work Details / Notes:</span>
                    <p className="text-slate-800 dark:text-slate-200 leading-relaxed">{selectedBooking.customer_notes}</p>
                  </div>
                )}

                <div className="pt-2 border-t border-slate-200 dark:border-slate-700 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-slate-400 block text-[10px]">Booking Mode</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200 capitalize">
                      {selectedBooking.booking_mode === 'on_demand' ? '⚡ On-demand' : '📅 Scheduled'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px]">Scheduled Time</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200">
                      {selectedBooking.scheduled_at 
                        ? new Date(selectedBooking.scheduled_at).toLocaleString() 
                        : 'Immediate / On-demand'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Advance Payment Details Card */}
              {(() => {
                const advReceived = Number(selectedBooking.advance_amount) || 0;
                const advApplied = Number(selectedBooking.advance_applied) || 0;
                const advRefunded = Number(selectedBooking.advance_refunded) || 0;
                const advRemaining = advReceived - advApplied - advRefunded;
                const canAddAdvance = selectedBooking.payment_status !== 'paid' && !['cancelled', 'rejected', 'no_show'].includes(selectedBooking.status);
                const canRefund = ['cancelled', 'no_show', 'rejected'].includes(selectedBooking.status) && advRemaining > 0;

                return (
                  <div className="p-4 rounded-xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/50 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-emerald-900 dark:text-emerald-300 uppercase tracking-wider flex items-center gap-1.5">
                        <IndianRupee size={13} />
                        <span>Advance Status</span>
                      </span>
                      {advRemaining > 0 ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300">
                          Bacha: ₹{advRemaining.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-500 dark:text-slate-400">
                          {advReceived > 0 ? 'Fully settled/refunded' : 'No advance'}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-white dark:bg-slate-900 p-2.5 rounded-lg border border-emerald-100 dark:border-emerald-900/40">
                        <span className="text-[9px] uppercase font-bold text-slate-400 block">Received</span>
                        <span className="text-xs font-mono font-bold text-slate-900 dark:text-white">
                          ₹{advReceived.toFixed(2)}
                        </span>
                        {selectedBooking.advance_mode && (
                          <span className="text-[9px] text-slate-500 capitalize block mt-0.5">
                            via {selectedBooking.advance_mode}
                          </span>
                        )}
                      </div>

                      <div className="bg-white dark:bg-slate-900 p-2.5 rounded-lg border border-emerald-100 dark:border-emerald-900/40">
                        <span className="text-[9px] uppercase font-bold text-slate-400 block">Adjusted</span>
                        <span className="text-xs font-mono font-bold text-slate-900 dark:text-white">
                          ₹{advApplied.toFixed(2)}
                        </span>
                      </div>

                      <div className="bg-white dark:bg-slate-900 p-2.5 rounded-lg border border-emerald-100 dark:border-emerald-900/40">
                        <span className="text-[9px] uppercase font-bold text-slate-400 block">Bacha (Held)</span>
                        <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400">
                          ₹{advRemaining.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Advance Action Buttons */}
                    <div className="flex items-center gap-2 pt-1">
                      {canAddAdvance && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setAddAdvanceAmount('');
                            setAddAdvanceMode('cash');
                            setShowAddAdvanceModal(true);
                          }}
                          className="h-8 text-xs font-semibold border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-950/60"
                        >
                          <Plus size={13} className="mr-1" />
                          <span>Add Advance</span>
                        </Button>
                      )}

                      {canRefund && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRefundAdvanceMode('cash');
                            setShowRefundAdvanceModal(true);
                          }}
                          className="h-8 text-xs font-semibold border-rose-300 dark:border-rose-800 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                        >
                          <IndianRupee size={13} className="mr-1" />
                          <span>Refund Advance (₹{advRemaining})</span>
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Task 3: Payment Visibility & Settlements Breakdown (RPC: get_booking_payments) */}
              <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="p-1 rounded bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                      <CreditCard size={13} />
                    </span>
                    <span className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                      Payment & Settlement Breakdown
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {bookingPayments && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded capitalize ${
                        bookingPayments.payment_status === 'paid'
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                      }`}>
                        {bookingPayments.payment_status || selectedBooking.payment_status || 'unpaid'}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => fetchBookingPayments(selectedBooking.id)}
                      disabled={loadingPayments}
                      className="p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/50"
                      title="Refresh payments"
                    >
                      <RefreshCw size={12} className={loadingPayments ? 'animate-spin' : ''} />
                    </button>
                  </div>
                </div>

                {loadingPayments && !bookingPayments ? (
                  <div className="py-4 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                    <Loader2 size={13} className="animate-spin" />
                    <span>Loading payment records...</span>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {/* Advance Summary */}
                    {Number(bookingPayments?.advance_amount || selectedBooking.advance_amount || 0) > 0 && (
                      <div className="bg-white dark:bg-slate-900 p-2.5 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs">
                        <div>
                          <span className="text-[10px] uppercase font-bold text-slate-400 block">Advance Recorded</span>
                          <span className="font-semibold text-slate-800 dark:text-slate-200">
                            via {bookingPayments?.advance_mode || selectedBooking.advance_mode || 'Cash'}
                          </span>
                          {bookingPayments?.advance_received_at && (
                            <span className="text-[10px] text-slate-400 block">
                              {new Date(bookingPayments.advance_received_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400 text-sm">
                          ₹{Number(bookingPayments?.advance_amount || selectedBooking.advance_amount || 0).toFixed(2)}
                        </span>
                      </div>
                    )}

                    {/* Online Gateway Payments List */}
                    {bookingPayments?.online && bookingPayments.online.length > 0 ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-[11px] font-semibold text-slate-600 dark:text-slate-300">
                          <span>Online Payments ({bookingPayments.online.length})</span>
                          <span className="font-mono font-bold text-slate-900 dark:text-white">
                            Total: ₹{Number(bookingPayments.online_total || 0).toFixed(2)}
                          </span>
                        </div>

                        <div className="space-y-2">
                          {bookingPayments.online.map((pay: any, idx: number) => {
                            const isSettled = pay.settlement_status === 'settled';
                            return (
                              <div 
                                key={pay.payment_id || idx}
                                className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800 space-y-2 text-xs"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="font-mono text-[11px] font-bold text-slate-800 dark:text-slate-200 truncate max-w-[200px]">
                                      {pay.payment_id}
                                    </div>
                                    <div className="text-[10px] text-slate-400 capitalize">
                                      {pay.purpose ? pay.purpose.replace(/_/g, ' ') : 'Online Payment'} • {new Date(pay.at).toLocaleString()}
                                    </div>
                                  </div>
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                                    isSettled 
                                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                                      : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                                  }`}>
                                    {isSettled ? '✓ Settled' : '⏳ Pending Settlement'}
                                  </span>
                                </div>

                                <div className="grid grid-cols-4 gap-1 pt-1.5 border-t border-slate-100 dark:border-slate-800 text-[10px]">
                                  <div>
                                    <span className="text-slate-400 block">Gross</span>
                                    <span className="font-mono font-bold text-slate-800 dark:text-slate-200">
                                      ₹{Number(pay.gross || 0).toFixed(2)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block">Fee/Comm</span>
                                    <span className="font-mono text-slate-500">
                                      -₹{(Number(pay.fee || 0) + Number(pay.platform_commission || 0)).toFixed(2)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-slate-400 block">Pro Share</span>
                                    <span className="font-mono text-slate-500">
                                      ₹{Number(pay.professional_share || 0).toFixed(2)}
                                    </span>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-slate-400 block">Merchant Net</span>
                                    <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                                      ₹{Number(pay.merchant_share || 0).toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white dark:bg-slate-900 p-3 rounded-lg border border-slate-200 dark:border-slate-800 text-center text-xs text-slate-400">
                        No online gateway payments recorded for this booking yet.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Customer Details */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                  <User size={13} className="text-primary" />
                  <span>Customer Details</span>
                </h4>
                <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Name</span>
                    <span className="font-semibold text-slate-900 dark:text-white">
                      {selectedBooking.customer_name}
                    </span>
                  </div>
                  {selectedBooking.customer_phone && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500">Phone</span>
                      <a 
                        href={`tel:${selectedBooking.customer_phone}`}
                        className="font-medium text-primary hover:underline flex items-center gap-1"
                      >
                        <Phone size={11} />
                        <span>{selectedBooking.customer_phone}</span>
                      </a>
                    </div>
                  )}
                  {selectedBooking.customer_address && (
                    <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
                      <span className="text-slate-500 block mb-0.5">Address</span>
                      <p className="text-slate-700 dark:text-slate-300 flex items-start gap-1">
                        <MapPin size={12} className="text-slate-400 mt-0.5 shrink-0" />
                        <span>{selectedBooking.customer_address}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Assigned Professional */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                  <Briefcase size={13} className="text-primary" />
                  <span>Assigned Professional</span>
                </h4>
                <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
                  {selectedBooking.professional_id ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                          {professionalLookup.get(selectedBooking.professional_id)?.full_name?.charAt(0) || 'P'}
                        </div>
                        <div>
                          <p className="font-bold text-slate-900 dark:text-white">
                            {professionalLookup.get(selectedBooking.professional_id)?.full_name || 'Assigned Staff'}
                          </p>
                          {professionalLookup.get(selectedBooking.professional_id)?.phone && (
                            <p className="text-[11px] text-slate-500">
                              {professionalLookup.get(selectedBooking.professional_id)?.phone}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Reassign option if status allows */}
                      {['requested', 'assigned'].includes(selectedBooking.status) && (
                        <select
                          value=""
                          onChange={e => {
                            if (e.target.value) {
                              setAssignProId(e.target.value);
                            }
                          }}
                          className="text-[11px] p-1 border rounded bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                        >
                          <option value="">Reassign...</option>
                          {professionals.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.full_name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-amber-600 dark:text-amber-400 font-semibold flex items-center gap-1">
                        <AlertCircle size={14} />
                        <span>No professional assigned yet</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Job Kit: Equipment & Raw Material Section */}
              {selectedBooking.professional_id && !['cancelled', 'rejected', 'no_show'].includes(selectedBooking.status) && (
                <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                  <JobKitManager
                    bookingId={selectedBooking.id}
                    professionalId={selectedBooking.professional_id}
                    mode="merchant"
                    onUpdated={loadData}
                  />
                </div>
              )}

              {/* Verification OTPs (Visible to Merchant to read out manually) */}
              <div className="p-4 rounded-xl bg-indigo-50/60 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800/60 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-indigo-900 dark:text-indigo-300 flex items-center gap-1.5">
                    <Key size={14} />
                    <span>Security Verification Codes</span>
                  </span>
                  <span className="text-[10px] text-indigo-600 dark:text-indigo-400">
                    Manual Dispatch Helper
                  </span>
                </div>
                <p className="text-[11px] text-indigo-700 dark:text-indigo-400">
                  Read these system OTPs out to the technician or customer to verify service start and completion:
                </p>

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="bg-white dark:bg-slate-900 p-2.5 rounded-lg border border-indigo-100 dark:border-slate-800 flex items-center justify-between">
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-slate-400 block font-semibold">Start OTP</span>
                      <span className="text-sm font-mono font-bold text-slate-900 dark:text-white">
                        {selectedBooking.start_otp || '— (Pending)'}
                      </span>
                    </div>
                    {selectedBooking.start_otp && (
                      <button
                        onClick={() => copyToClipboard(selectedBooking.start_otp!, 'Start OTP')}
                        className="text-slate-400 hover:text-primary p-1"
                        title="Copy Start OTP"
                      >
                        <Copy size={13} />
                      </button>
                    )}
                  </div>

                  <div className="bg-white dark:bg-slate-900 p-2.5 rounded-lg border border-indigo-100 dark:border-slate-800 flex items-center justify-between">
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-slate-400 block font-semibold">Complete OTP</span>
                      <span className="text-sm font-mono font-bold text-slate-900 dark:text-white">
                        {selectedBooking.complete_otp || '— (Pending)'}
                      </span>
                    </div>
                    {selectedBooking.complete_otp && (
                      <button
                        onClick={() => copyToClipboard(selectedBooking.complete_otp!, 'Complete OTP')}
                        className="text-slate-400 hover:text-primary p-1"
                        title="Copy Complete OTP"
                      >
                        <Copy size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Status Timeline History */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                  <Clock size={13} className="text-primary" />
                  <span>Activity Timestamps</span>
                </h4>
                <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 text-[11px] space-y-1.5 text-slate-600 dark:text-slate-300">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Created:</span>
                    <span className="font-mono">{new Date(selectedBooking.created_at).toLocaleString()}</span>
                  </div>
                  {selectedBooking.assigned_at && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Assigned:</span>
                      <span className="font-mono">{new Date(selectedBooking.assigned_at).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedBooking.accepted_at && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Accepted:</span>
                      <span className="font-mono">{new Date(selectedBooking.accepted_at).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedBooking.started_at && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Started:</span>
                      <span className="font-mono">{new Date(selectedBooking.started_at).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedBooking.completed_at && (
                    <div className="flex justify-between text-emerald-600 font-semibold">
                      <span>Completed:</span>
                      <span className="font-mono">{new Date(selectedBooking.completed_at).toLocaleString()}</span>
                    </div>
                  )}
                  {selectedBooking.cancelled_at && (
                    <div className="pt-1 border-t border-slate-100 dark:border-slate-800">
                      <div className="flex justify-between text-rose-600 font-semibold">
                        <span>Cancelled:</span>
                        <span className="font-mono">{new Date(selectedBooking.cancelled_at).toLocaleString()}</span>
                      </div>
                      {selectedBooking.cancelled_reason && (
                        <p className="mt-1 text-slate-500 italic bg-rose-50 dark:bg-rose-950/40 p-2 rounded-md">
                          Reason: {selectedBooking.cancelled_reason}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Task 2 & 6: Customer Messages (WhatsApp) & Timeline Panel */}
              <ServiceBookingMessagesPanel
                bookingId={selectedBooking.id}
                onRefresh={loadData}
              />
            </div>

            {/* Task 1: Action Buttons Section: Job steps (office) */}
            <div className="p-5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/80 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                  Job steps (office)
                </span>
                {selectedBookingHasOfficeEvent && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                    Office-managed
                  </span>
                )}
              </div>

              {/* STATE = requested */}
              {selectedBooking.status === 'requested' && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Assign Professional
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={assignProId}
                        onChange={e => setAssignProId(e.target.value)}
                        className="flex-1 h-9 px-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs"
                      >
                        <option value="">Select a Professional...</option>
                        {professionals.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.full_name} {p.phone ? `(${p.phone})` : ''}
                          </option>
                        ))}
                      </select>
                      <Button
                        onClick={handleAssignProfessional}
                        disabled={!assignProId || actionLoading}
                        className="bg-primary hover:bg-primary/90 text-white text-xs h-9 px-4 shrink-0"
                      >
                        {actionLoading ? <Loader2 size={13} className="animate-spin" /> : 'Assign'}
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCancelModal(true)}
                      className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                    >
                      <Ban size={13} className="mr-1" />
                      <span>Cancel Booking</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* STATE = assigned */}
              {selectedBooking.status === 'assigned' && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500">
                    Technician assigned. Office can mark accepted or cancel on behalf of staff:
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleOfficeMarkAccepted}
                      disabled={officeActionLoading}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9 font-semibold"
                    >
                      {officeActionLoading ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} className="mr-1" />}
                      <span>Mark accepted</span>
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => setShowCancelModal(true)}
                      disabled={officeActionLoading}
                      className="text-xs h-9 text-rose-600 hover:bg-rose-50 border-rose-200"
                    >
                      <span>Cancel booking</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* STATE = accepted */}
              {selectedBooking.status === 'accepted' && (() => {
                const chargeDue = Number(selectedBooking.booking_charge_due || 0);
                const advAmt = Number(selectedBooking.advance_amount || 0);
                const isWaived = Boolean(selectedBooking.booking_charge_waived);
                const chargeRem = isWaived ? 0 : Math.max(0, chargeDue - advAmt);
                const isPaymentPending = chargeRem > 0;

                return (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500">
                      Booking accepted. Start work from office (with customer OTP or override reason):
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        disabled={isPaymentPending || officeActionLoading}
                        onClick={() => {
                          setOfficeOtpInput('');
                          setOfficeReasonSelect('Professional has no smartphone');
                          setOfficeReasonCustom('');
                          setShowOfficeModal({ action: 'start', targetBooking: selectedBooking });
                        }}
                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs h-10 font-semibold disabled:opacity-50"
                        title={isPaymentPending ? 'Booking payment pending' : undefined}
                      >
                        <Play size={14} className="mr-1.5 fill-current" />
                        <span>Start work</span>
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCancelModal(true)}
                        className="text-xs text-rose-600 hover:bg-rose-50 h-10"
                      >
                        Cancel booking
                      </Button>
                    </div>
                    {isPaymentPending && (
                      <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1 font-medium">
                        <AlertCircle size={12} />
                        Booking payment pending (₹{chargeRem.toFixed(2)} due). Collect advance to enable start.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* STATE = in_progress */}
              {selectedBooking.status === 'in_progress' && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500">
                    Service actively ongoing. Complete work from office (with customer OTP or override reason):
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => {
                        setOfficeOtpInput('');
                        setOfficeReasonSelect('Professional has no smartphone');
                        setOfficeReasonCustom('');
                        setOfficeFinalPrice(String(selectedBooking.quoted_price || ''));
                        setShowOfficeModal({ action: 'complete', targetBooking: selectedBooking });
                      }}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-10 font-semibold"
                    >
                      <CheckCircle2 size={15} className="mr-1.5" />
                      <span>Complete work</span>
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCancelModal(true)}
                      className="text-xs text-rose-600 hover:bg-rose-50 h-10"
                    >
                      Cancel booking
                    </Button>
                  </div>
                </div>
              )}

              {/* Services Smart Billing: Materials, Unit Tracking, Discount & GST Invoice */}
              {(['in_progress', 'completed'].includes(selectedBooking.status) || Boolean(selectedBooking.invoice_id)) && (
                <div className="pt-3 border-t border-slate-200 dark:border-slate-800">
                  <ServiceBillingPanel
                    bookingId={selectedBooking.id}
                    bookingStatus={selectedBooking.status}
                    invoiceId={selectedBooking.invoice_id}
                    role="staff"
                    onChanged={loadData}
                  />
                </div>
              )}

              {/* Terminal States */}
              {['completed', 'cancelled', 'rejected', 'no_show'].includes(selectedBooking.status) && (
                <div className="text-center py-1">
                  <span className="text-xs text-slate-400 font-medium">
                    This booking has reached its final state ({selectedBooking.status}). No further status transitions allowed.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* Start OTP Modal                                           */}
      {/* ========================================================= */}
      {showStartOtpModal && selectedBooking && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-purple-600 mb-3">
              <div className="p-2.5 rounded-xl bg-purple-50 dark:bg-purple-950/50">
                <Play size={20} className="fill-current" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Start Service</h3>
                <p className="text-[11px] text-slate-500">Verify customer presence</p>
              </div>
            </div>

            <form onSubmit={handleStartService} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Enter Start OTP <span className="text-rose-500">*</span>
                </label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="e.g. 4821"
                  value={startOtpInput}
                  onChange={e => setStartOtpInput(e.target.value)}
                  required
                  autoFocus
                  className="h-11 text-center font-mono text-lg tracking-widest font-bold"
                />
                <span className="text-[10px] text-slate-400 mt-1 block">
                  The backend validates this OTP against the booking's generated start code.
                </span>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowStartOtpModal(false)}
                  disabled={actionLoading}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading || !startOtpInput}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-4"
                >
                  {actionLoading ? <Loader2 size={13} className="animate-spin" /> : 'Confirm & Start'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* Complete Service Modal                                    */}
      {/* ========================================================= */}
      {showCompleteModal && selectedBooking && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-emerald-600 mb-3">
              <div className="p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/50">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Complete Service</h3>
                <p className="text-[11px] text-slate-500">Verify completion code & final price</p>
              </div>
            </div>

            <form onSubmit={handleCompleteService} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Enter Completion OTP <span className="text-rose-500">*</span>
                </label>
                <Input
                  type="text"
                  maxLength={6}
                  placeholder="e.g. 7395"
                  value={completeOtpInput}
                  onChange={e => setCompleteOtpInput(e.target.value)}
                  required
                  autoFocus
                  className="h-11 text-center font-mono text-lg tracking-widest font-bold"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Final Billed Price (₹)
                </label>
                <div className="relative">
                  <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="e.g. 350"
                    value={finalPriceInput}
                    onChange={e => setFinalPriceInput(e.target.value)}
                    className="pl-9 h-10 text-sm font-bold"
                  />
                </div>
                <span className="text-[10px] text-slate-400 mt-1 block">
                  Defaults to quoted price (₹{selectedBooking.quoted_price}), update if custom materials or extra charges apply.
                </span>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCompleteModal(false)}
                  disabled={actionLoading}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading || !completeOtpInput}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4"
                >
                  {actionLoading ? <Loader2 size={13} className="animate-spin" /> : 'Confirm & Complete'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* Cancel Booking Modal                                      */}
      {/* ========================================================= */}
      {showCancelModal && selectedBooking && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-rose-500 mb-3">
              <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/50">
                <Ban size={20} />
              </div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Cancel Booking</h3>
            </div>

            <form onSubmit={handleCancelBooking} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Cancellation Reason <span className="text-rose-500">*</span>
                </label>
                <Textarea
                  placeholder="e.g. Customer requested cancellation / Staff unavailable"
                  value={cancelReasonInput}
                  onChange={e => setCancelReasonInput(e.target.value)}
                  rows={3}
                  required
                  autoFocus
                  className="text-xs resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCancelModal(false)}
                  disabled={actionLoading}
                  className="text-xs"
                >
                  Close
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading || !cancelReasonInput.trim()}
                  className="bg-rose-600 hover:bg-rose-700 text-white text-xs px-4"
                >
                  {actionLoading ? <Loader2 size={13} className="animate-spin" /> : 'Confirm Cancel'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* Add Advance Payment Modal                                 */}
      {/* ========================================================= */}
      {showAddAdvanceModal && selectedBooking && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-emerald-600 mb-3">
              <div className="p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/50">
                <IndianRupee size={20} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Record Advance Payment</h3>
                <p className="text-[11px] text-slate-500">Booking #{selectedBooking.booking_number}</p>
              </div>
            </div>

            <form onSubmit={handleRecordAdvance} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Amount Received (₹) <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 500"
                    value={addAdvanceAmount}
                    onChange={e => {
                      if (e.target.value === '' || /^\d*\.?\d*$/.test(e.target.value)) {
                        setAddAdvanceAmount(e.target.value);
                      }
                    }}
                    required
                    autoFocus
                    className="pl-8 h-10 text-sm font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Payment Mode
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setAddAdvanceMode('cash')}
                    className={`py-2 text-xs font-bold rounded-lg border transition-all ${
                      addAdvanceMode === 'cash'
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    Cash
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddAdvanceMode('online')}
                    className={`py-2 text-xs font-bold rounded-lg border transition-all ${
                      addAdvanceMode === 'online'
                        ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    Online / UPI
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddAdvanceModal(false)}
                  disabled={submittingAdvance}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submittingAdvance || !addAdvanceAmount}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4"
                >
                  {submittingAdvance ? <Loader2 size={13} className="animate-spin" /> : 'Record Advance'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* Refund Advance Payment Modal                              */}
      {/* ========================================================= */}
      {showRefundAdvanceModal && selectedBooking && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-rose-500 mb-3">
              <div className="p-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/50">
                <IndianRupee size={20} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Refund Advance</h3>
                <p className="text-[11px] text-slate-500">Booking #{selectedBooking.booking_number}</p>
              </div>
            </div>

            <form onSubmit={handleRefundAdvance} className="space-y-4">
              <div className="p-3 rounded-xl bg-rose-50/60 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/40 text-xs">
                <span className="text-slate-500 block mb-0.5">Bacha Advance jo refund hoga:</span>
                <span className="text-lg font-bold font-mono text-rose-600 dark:text-rose-400">
                  ₹{(
                    (Number(selectedBooking.advance_amount) || 0) -
                    (Number(selectedBooking.advance_applied) || 0) -
                    (Number(selectedBooking.advance_refunded) || 0)
                  ).toFixed(2)}
                </span>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Refund Payment Mode
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setRefundAdvanceMode('cash')}
                    className={`py-2 text-xs font-bold rounded-lg border transition-all ${
                      refundAdvanceMode === 'cash'
                        ? 'bg-rose-600 text-white border-rose-600 shadow-xs'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    Cash
                  </button>
                  <button
                    type="button"
                    onClick={() => setRefundAdvanceMode('online')}
                    className={`py-2 text-xs font-bold rounded-lg border transition-all ${
                      refundAdvanceMode === 'online'
                        ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    Online / UPI
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRefundAdvanceModal(false)}
                  disabled={submittingRefund}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submittingRefund}
                  className="bg-rose-600 hover:bg-rose-700 text-white text-xs px-4"
                >
                  {submittingRefund ? <Loader2 size={13} className="animate-spin" /> : 'Confirm Refund'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* New Booking Form Modal                                    */}
      {/* ========================================================= */}
      {showNewBookingModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
            <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Create New Booking</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Schedule or dispatch on-demand service appointment
                </p>
              </div>
              <button
                onClick={() => setShowNewBookingModal(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateBooking} className="overflow-y-auto p-6 space-y-5">
              {/* Overall Work Details / Notes */}
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Overall Work Details / Notes
                </label>
                <Textarea
                  placeholder="Service requirements, customer instructions or notes (e.g. 2nd floor flat, carry ladder)..."
                  value={newNotes}
                  onChange={e => setNewNotes(e.target.value)}
                  rows={2}
                  className="text-xs resize-none"
                />
              </div>

              {/* Multiple Service Lines */}
              <div className="space-y-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                    Services ({serviceLines.length})
                  </span>
                  {serviceLines.length < 20 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAddServiceLine}
                      className="h-7 text-[11px] px-2.5 text-primary border-primary/30 hover:bg-primary/10 gap-1 font-semibold"
                    >
                      <Plus size={13} />
                      <span>Add another service</span>
                    </Button>
                  )}
                </div>

                <div className="space-y-3">
                  {serviceLines.map((line, idx) => {
                    const selItem = catalogLookup.get(line.catalog_item_id);
                    const unitName = selItem?.unit_id ? unitMap.get(selItem.unit_id) : null;
                    const isCustom = Boolean(selItem?.allow_custom_price);
                    const qtyNum = parseFloat(line.quantity) || 0;
                    const lineTotal = isCustom 
                      ? (parseFloat(line.custom_total) || 0)
                      : (selItem ? (selItem.base_price ?? 0) * qtyNum : 0);

                    return (
                      <div 
                        key={line.id} 
                        className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2.5 shadow-2xs"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                            Service #{idx + 1}
                          </span>
                          {serviceLines.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveServiceLine(line.id)}
                              className="p-1 rounded text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                              title="Remove service line"
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>

                        {/* Service Item Select */}
                        <div>
                          <select
                            value={line.catalog_item_id}
                            onChange={e => handleLineChange(line.id, 'catalog_item_id', e.target.value)}
                            required
                            className="w-full h-9 px-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs outline-none focus:ring-1 focus:ring-primary"
                          >
                            <option value="">Select a service...</option>
                            {catalogItems.map(item => (
                              <option key={item.id} value={item.id}>
                                {item.service_name} ({item.allow_custom_price ? 'Custom Price' : `₹${item.base_price}/unit`})
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Quantity + Unit + Price Total */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          <div>
                            <label className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                              Quantity {unitName ? `(${unitName})` : ''} <span className="text-rose-500">*</span>
                            </label>
                            <div className="relative">
                              <Input
                                type="text"
                                inputMode="decimal"
                                placeholder="e.g. 1 ya 1.5"
                                value={line.quantity}
                                onChange={e => handleLineChange(line.id, 'quantity', e.target.value)}
                                className="h-9 text-xs font-bold"
                              />
                              {unitName && (
                                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                                  {unitName}
                                </span>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                              {isCustom ? 'Custom Total Price (₹)' : 'Line Total (Live)'}
                            </label>
                            {isCustom ? (
                              <div className="relative">
                                <IndianRupee size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  placeholder="e.g. 500"
                                  value={line.custom_total}
                                  onChange={e => {
                                    if (e.target.value === '' || /^\d*\.?\d*$/.test(e.target.value)) {
                                      handleLineChange(line.id, 'custom_total', e.target.value);
                                    }
                                  }}
                                  className="h-9 pl-7 text-xs font-bold"
                                />
                              </div>
                            ) : (
                              <div className="h-9 px-3 rounded-lg bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs font-mono font-bold">
                                <span className="text-slate-500 text-[10px] font-sans font-normal">
                                  {line.quantity.trim() !== '' ? `₹${selItem?.base_price || 0} x ${line.quantity}` : 'Blank qty'}
                                </span>
                                <span className="text-slate-900 dark:text-white">
                                  {line.quantity.trim() !== '' ? `₹${lineTotal.toFixed(2)}` : '—'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Line specific note */}
                        <div>
                          <Input
                            placeholder="Line note (e.g. outdoor unit cleaning only)"
                            value={line.notes}
                            onChange={e => handleLineChange(line.id, 'notes', e.target.value)}
                            className="h-8 text-[11px]"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Grand Total Display */}
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    Grand Total ({serviceLines.length} {serviceLines.length === 1 ? 'service' : 'services'}):
                  </span>
                  <span className="text-base font-bold font-mono text-primary">
                    ₹{calculatedGrandTotal.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Customer Search Dropdown */}
              {currentTenantId && (
                <ServiceCustomerSearchDropdown
                  tenantId={currentTenantId}
                  customerName={newCustomerName}
                  customerPhone={newCustomerPhone}
                  customerAddress={newCustomerAddress}
                  selectedCustomerId={selectedCustomerId}
                  onSelectCustomer={(cust) => {
                    setSelectedCustomerId(cust.id);
                    setNewCustomerName(cust.customer_name || '');
                    setNewCustomerPhone(cust.phone || '');
                    setNewCustomerAddress(cust.address || '');
                  }}
                  onChangeName={(val) => {
                    setNewCustomerName(val);
                    setSelectedCustomerId(null);
                  }}
                  onChangePhone={(val) => {
                    setNewCustomerPhone(val);
                    setSelectedCustomerId(null);
                  }}
                  onChangeAddress={(val) => setNewCustomerAddress(val)}
                  onClearCustomer={() => {
                    setSelectedCustomerId(null);
                    setNewCustomerName('');
                    setNewCustomerPhone('');
                    setNewCustomerAddress('');
                  }}
                />
              )}

              {/* Advance Payment Section (Optional) */}
              <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
                    <IndianRupee size={13} className="text-primary" />
                    <span>Advance Payment (Optional)</span>
                  </span>
                  {advanceAmount && parseFloat(advanceAmount) > calculatedGrandTotal && calculatedGrandTotal > 0 && (
                    <span className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded border border-amber-200 dark:border-amber-800">
                      Advance &gt; Total
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Advance Amount (₹)
                    </label>
                    <div className="relative">
                      <IndianRupee size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="e.g. 200 (optional)"
                        value={advanceAmount}
                        onChange={e => {
                          if (e.target.value === '' || /^\d*\.?\d*$/.test(e.target.value)) {
                            setAdvanceAmount(e.target.value);
                          }
                        }}
                        className="pl-7 h-9 text-xs font-bold"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Advance Mode
                    </label>
                    <div className="grid grid-cols-2 gap-1.5 h-9">
                      <button
                        type="button"
                        onClick={() => setAdvanceMode('cash')}
                        className={`text-xs font-bold rounded-lg border transition-all ${
                          advanceMode === 'cash'
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                            : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        Cash
                      </button>
                      <button
                        type="button"
                        onClick={() => setAdvanceMode('online')}
                        className={`text-xs font-bold rounded-lg border transition-all ${
                          advanceMode === 'online'
                            ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                            : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        Online / UPI
                      </button>
                    </div>
                  </div>
                </div>

                {/* Baaki Balance Live Display */}
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-xs">
                  <span className="text-slate-500">
                    Baaki (Due upon completion):
                  </span>
                  <span className="font-mono font-bold text-slate-900 dark:text-white">
                    ₹{Math.max(0, calculatedGrandTotal - (parseFloat(advanceAmount) || 0)).toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Booking Mode: Toggle Scheduled vs On-Demand */}
              <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5">
                    Booking Mode
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setNewBookingMode('on_demand')}
                      className={`py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all border ${
                        newBookingMode === 'on_demand'
                          ? 'bg-primary text-white border-primary shadow-xs'
                          : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      <Zap size={14} />
                      <span>On-Demand (Now)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewBookingMode('scheduled')}
                      className={`py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all border ${
                        newBookingMode === 'scheduled'
                          ? 'bg-primary text-white border-primary shadow-xs'
                          : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      <Calendar size={14} />
                      <span>Scheduled Slot</span>
                    </button>
                  </div>
                </div>

                {newBookingMode === 'scheduled' && (
                  <div>
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Scheduled Date & Time <span className="text-rose-500">*</span>
                    </label>
                    <Input
                      type="datetime-local"
                      value={newScheduledAt}
                      onChange={e => setNewScheduledAt(e.target.value)}
                      required={newBookingMode === 'scheduled'}
                      className="h-10 text-xs"
                    />
                  </div>
                )}
              </div>

              {/* Assign Professional */}
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Assign Professional Now (Optional)
                </label>
                <select
                  value={newProfessionalId}
                  onChange={e => setNewProfessionalId(e.target.value)}
                  className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Leave Unassigned (Assign Later)</option>
                  {professionals.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.full_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowNewBookingModal(false)}
                  disabled={creatingBooking}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creatingBooking}
                  className="bg-primary hover:bg-primary/90 text-white text-xs font-semibold px-5"
                >
                  {creatingBooking ? (
                    <div className="flex items-center gap-1.5">
                      <Loader2 size={13} className="animate-spin" />
                      <span>Creating Booking...</span>
                    </div>
                  ) : (
                    `Create Booking (₹${calculatedGrandTotal.toFixed(2)})`
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Booking Success with Pending Minimum Charge Modal */}
      {createdSuccessBooking && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-150 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={28} />
            </div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white">
              Booking Created Successfully!
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Booking #{createdSuccessBooking.bookingNumber} for {createdSuccessBooking.customerName}
            </p>

            <div className="my-4 p-3.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-left">
              <div className="text-[11px] font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wider">
                Minimum Booking Charge Due
              </div>
              <div className="text-lg font-extrabold font-mono text-slate-900 dark:text-white mt-0.5">
                ₹{createdSuccessBooking.chargeRemaining.toFixed(2)}
              </div>
              <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-1">
                Generate an online payment link (UPI, QR, Card) and send it directly to customer on WhatsApp to confirm booking.
              </p>
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <Button
                type="button"
                onClick={handleCreateAndSendWhatsAppForNewBooking}
                disabled={creatingAndSendingWhatsApp}
                className="w-full h-10 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white gap-2 shadow-sm"
              >
                {creatingAndSendingWhatsApp ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                <span>Create link & send on WhatsApp</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreatedSuccessBooking(null)}
                disabled={creatingAndSendingWhatsApp}
                className="w-full h-9 text-xs text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-800"
              >
                Skip
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Office Action Modal (Start / Complete Work without OTP or with override) */}
      {showOfficeModal && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className={`p-2 rounded-xl ${
                  showOfficeModal.action === 'start' 
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300' 
                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                }`}>
                  {showOfficeModal.action === 'start' ? <Play size={18} className="fill-current" /> : <CheckCircle2 size={18} />}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                    {showOfficeModal.action === 'start' ? 'Start Work (Office Managed)' : 'Complete Work & Auto-Generate Bill'}
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Booking #{showOfficeModal.targetBooking.booking_number} • {showOfficeModal.targetBooking.customer_name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowOfficeModal(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleOfficeActionSubmit} className="space-y-4">
              {/* Optional OTP */}
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Customer Verification OTP <span className="text-[11px] font-normal text-slate-400">(Optional)</span>
                </label>
                <Input
                  type="text"
                  placeholder="e.g. 1234 (leave blank for office override)"
                  value={officeOtpInput}
                  onChange={e => setOfficeOtpInput(e.target.value)}
                  className="h-9 text-xs font-mono font-bold"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  If customer provided OTP, enter it here. Otherwise, leave blank to proceed as office managed.
                </p>
              </div>

              {/* Optional Reason if OTP is empty */}
              {!officeOtpInput.trim() && (
                <div className="space-y-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block">
                    Office Management Reason <span className="text-[11px] font-normal text-slate-400">(Optional)</span>
                  </label>
                  <select
                    value={officeReasonSelect}
                    onChange={e => setOfficeReasonSelect(e.target.value)}
                    className="w-full h-8 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-2 outline-none"
                  >
                    <option value="Managed from office">Managed from office</option>
                    <option value="Professional has no smartphone">Professional has no smartphone</option>
                    <option value="Customer phone battery dead">Customer phone battery dead</option>
                    <option value="Customer requested direct offline verification">Customer requested direct offline verification</option>
                    <option value="Other">Other reason...</option>
                  </select>

                  {officeReasonSelect === 'Other' && (
                    <Input
                      type="text"
                      placeholder="Type custom reason..."
                      value={officeReasonCustom}
                      onChange={e => setOfficeReasonCustom(e.target.value)}
                      className="h-8 text-xs bg-white dark:bg-slate-800"
                    />
                  )}
                </div>
              )}

              {/* If Complete: Final Price */}
              {showOfficeModal.action === 'complete' && (
                <div>
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Final Price (₹)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g. 500"
                    value={officeFinalPrice}
                    onChange={e => setOfficeFinalPrice(e.target.value)}
                    className="h-9 text-xs font-bold font-mono"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    Auto bill (invoice) will be generated with this amount. Quoted: ₹{showOfficeModal.targetBooking.quoted_price || 0}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOfficeModal(null)}
                  disabled={officeActionLoading}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={officeActionLoading}
                  className={`text-xs px-4 text-white font-semibold ${
                    showOfficeModal.action === 'start'
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  {officeActionLoading ? (
                    <div className="flex items-center gap-1.5">
                      <Loader2 size={13} className="animate-spin" />
                      <span>Processing...</span>
                    </div>
                  ) : showOfficeModal.action === 'start' ? (
                    'Confirm Start'
                  ) : (
                    'Complete & Auto Bill'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Blueprint Live Bill Print & Preview Modal */}
      {showBillPrintBookingId && (
        <ServiceBillPrintModal
          bookingId={showBillPrintBookingId}
          isOpen={!!showBillPrintBookingId}
          onClose={() => setShowBillPrintBookingId(null)}
        />
      )}
    </div>
  );
}
